import type { Building } from "building-types/building/buildingTypes";
import type { ParametricPosition } from "building-types/columns/porchColumnPattern";
import type { FoundationPatternEdgeBehavior } from "building-types/edgeBehavior/edgeBehavior";
import { FoundationSectionSizeType } from "building-types/foundations/foundationEnums";
import type {
  GeneratedIsolatedFoundationPattern,
  IsolatedFoundationPoint,
} from "building-types/foundations/foundationPatternTypes";
import { NearestMeasurementMethod } from "building-types/foundations/foundationPatternTypes";
import type { Level } from "building-types/levels/levelTypes";
import type {
  LevelHalfEdgeAssociationTable,
  RWLevelHalfEdgeAssociationTable,
} from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { GeneratedStairSystem } from "building-types/stairs/stairTypes";
import type { Table } from "common/data/table";
import { ensureDefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { docEntityTableFilter } from "doc/entity/docEntityTableFilter";
import { line2PerpDistance } from "geom/line2";
import type { PlanarGraph } from "geom/planarGraph";
import { planarGraphHalfEdgeEnd, planarGraphHalfEdgeStart } from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2Centroid, polyLine2Edge } from "geom/polyLine2";
import { vec2AngleBetween, vec2DistSquared, vec2Dot, vec2IsInEdgeBounds, vec2Len } from "geom/vec2";
import { bottomLevelId, groundLevelIds } from "../level/levelTools";
import { isolatedFoundationPatternNew, pierParamPositionOnEdge, resolvePostCentroid } from "./foundationPattern";

/**
 * this function returns an array of isolated foundation points. (see commented function
 * above for data push) the function is intended to collect all the points from all the
 * levels which needs an isolated foundation below.
 */
export function collectIsolatedFoundationPointsFromStairSystems(
  levels: Table<Level>,
  stairSystems: Table<GeneratedStairSystem>,
  foundationPatternEdgeBehaviors: Table<FoundationPatternEdgeBehavior>,
): IsolatedFoundationPoint[] {
  const isolatedFoundationPoints: IsolatedFoundationPoint[] = [];

  const bottomLevel = bottomLevelId(levels);

  for (const stairSystemId in stairSystems) {
    const { blocks = {} } = stairSystems[stairSystemId];
    for (const blockId in blocks) {
      const { edgeIds, outline, id } = blocks[blockId];
      const foundationPatterns = Object.values(
        docEntityTableFilter(foundationPatternEdgeBehaviors, (x) => x.parentId === id).newTable,
      );

      for (const { foundationPattern, edgeId, disabled } of foundationPatterns) {
        if (disabled) {
          //stairSystems already evaluated
          continue;
        }

        if (foundationPattern.patternType !== FoundationSectionSizeType.ISOLATED) {
          continue;
        }

        if (foundationPattern.hostType !== "STAIR_BLOCK") {
          continue;
        }

        const { assemblyId, topDatumId, bottomDatumId } = foundationPattern;

        const edge = polyLine2Edge(outline, edgeIds.indexOf(edgeId));

        const [startX, startY, endX, endY] = edge;

        const edgeAngle = Math.atan2(endY - startY, endX - startX);

        const { foundationLevelId = bottomLevel } = foundationPattern;

        if (!foundationLevelId) {
          continue;
        }

        for (const position of foundationPattern.positions) {
          const [x, y] = resolvePostCentroid(edge, foundationPattern, position);

          isolatedFoundationPoints.push({
            x,
            y,
            foundationAssemblyId: assemblyId,
            foundationLevelId,
            edgeAngle,
            topDatumId,
            bottomDatumId,
          });
        }
      }
    }
  }
  return isolatedFoundationPoints;
}

/**
 *
 * @param columns the columns to collect isolated foundation points from
 * @param foundationLevelIds foundation level ids
 * @param levels table of levels
 * @returns array of isolated foundation points
 */
export function collectIsolatedFoundationPointsFromColumns(
  columns: Building["columns"],
  foundationLevelIds: string[],
  rooms: Building["rooms"],
  levels: Building["levels"],
): IsolatedFoundationPoint[] {
  const isolatedFoundationPoints: IsolatedFoundationPoint[] = [];
  let foundationLevelId: string | undefined;
  for (const columnIdx in columns) {
    const { planProfiles, isolatedAssemblyId, levelId, roomId, edgeId, pickHandle, handle } = columns[columnIdx];

    if (isolatedAssemblyId === undefined || rooms[roomId] === undefined || planProfiles.length === 0) {
      continue;
    }
    const { outline, edgeIds } = rooms[roomId];

    const edgeIdx = edgeIds.indexOf(edgeId);

    const edge = polyLine2Edge(outline, edgeIdx);

    const [startX, startY, endX, endY] = edge;

    const edgeAngle = Math.atan2(endY - startY, endX - startX);

    if (foundationLevelId !== levelId) {
      foundationLevelId = levelId;
      if (!foundationLevelIds.includes(levelId)) {
        //find the closest foundation level to get half edge from which to generate isolated foundation points
        const columnLevel = levels[levelId];
        const columnLevelElevation = columnLevel.floorElevation;
        let closestFoundationLevelElevation = -Infinity;
        for (const foundationLevelIdx of foundationLevelIds) {
          const foundationLevel = levels[foundationLevelIdx];
          const foundationLevelElevation = foundationLevel.floorElevation;
          if (
            foundationLevelElevation < columnLevelElevation &&
            foundationLevelElevation > closestFoundationLevelElevation
          ) {
            closestFoundationLevelElevation = foundationLevelElevation;
            foundationLevelId = foundationLevelIdx;
          }
        }
      }
    }

    const [xcenter, ycenter] = ensureDefined(polyLine2Centroid(planProfiles[0]));
    isolatedFoundationPoints.push({
      x: xcenter,
      y: ycenter,
      foundationLevelId,
      foundationAssemblyId: isolatedAssemblyId,
      edgeAngle,
      sourcePickHandle: pickHandle ?? handle,
    });
  }
  return isolatedFoundationPoints;
}

const ROUND_FACT = 100000;
const PATTERN_POINT_HASH_DELIMETER = "#.#";
interface PatternPoint extends IsolatedFoundationPoint {
  halfEdgeIndex: number;
  midDistSqrd: number;
  perpDist: number;
  rel: number;
  rotAngleFromBaseline: number;
}

function hashPatternPoint(pt: PatternPoint, delimeter = PATTERN_POINT_HASH_DELIMETER) {
  const {
    perpDist,
    foundationAssemblyId,
    foundationLevelId,
    rotAngleFromBaseline,
    topDatumId,
    bottomDatumId,
    sourcePickHandle,
  } = pt;
  const sourcePickHandleHash = sourcePickHandle ? docEntityHandleHash(sourcePickHandle) : "";
  return [
    perpDist,
    foundationAssemblyId,
    foundationLevelId,
    rotAngleFromBaseline,
    topDatumId,
    bottomDatumId,
    sourcePickHandleHash,
  ].join(delimeter);
}

function unHashPatternPoint(hash: string, delimeter = PATTERN_POINT_HASH_DELIMETER) {
  const [perpDist, foundationAssemblyId, foundationLevelId, rotAngleFromBaseline, topDatumId, bottomDatumId] =
    hash.split(delimeter);
  return {
    perpDist: parseFloat(perpDist),
    foundationAssemblyId,
    foundationLevelId,
    rotAngleFromBaseline: parseFloat(rotAngleFromBaseline),
    topDatumId,
    bottomDatumId,
  };
}

export function makeAutogeneratedIsolatedFoundationPatterns(
  foundationLevelIds: string[],
  planarGraphs: Table<PolygonPlanarGraph>,
  isolatedFoundationPoints: IsolatedFoundationPoint[],
): LevelHalfEdgeAssociationTable<GeneratedIsolatedFoundationPattern> {
  const foundationPatterns: RWLevelHalfEdgeAssociationTable<GeneratedIsolatedFoundationPattern> = {};
  const nearestHalfEdges: { [halfEdgeIndex: number]: { [hash: string]: PatternPoint[] } } = {};

  for (const isolatedFoundationPt of isolatedFoundationPoints) {
    const { x, y, foundationLevelId: levelId } = isolatedFoundationPt;

    //make sure the foundationLevelId in isolatedFoundationPt is a valid foundationLevelId
    if (!foundationLevelIds.includes(levelId)) {
      continue;
    }

    const levelGraph = planarGraphs[levelId];
    const { halfEdges } = levelGraph.planarGraph;
    const p: PatternPoint = {
      halfEdgeIndex: -1,
      midDistSqrd: Infinity,
      perpDist: 0,
      rel: 0,
      rotAngleFromBaseline: 0,
      ...isolatedFoundationPt,
    };

    //find the closest half edge
    for (let i = 0, l = halfEdges.length; i < l; i += 2) {
      const halfEdgeIndex = i / 2;
      const [x1, y1] = planarGraphHalfEdgeStart(levelGraph.planarGraph, halfEdgeIndex);
      const [x2, y2] = planarGraphHalfEdgeEnd(levelGraph.planarGraph, halfEdgeIndex);
      const [xmid, ymid] = [(x1 + x2) / 2, (y1 + y2) / 2];
      const d = vec2DistSquared(xmid, ymid, x, y);

      if (p.midDistSqrd > d) {
        p.midDistSqrd = d;
        p.halfEdgeIndex = halfEdgeIndex;
        const angle = vec2AngleBetween(x2 - x1, y2 - y1, x - x1, y - y1);
        const length = vec2Len(x - x1, y - y1);
        p.perpDist = length * Math.sin(angle);
      }
    }
    if (p.halfEdgeIndex !== -1) {
      const [x1, y1] = planarGraphHalfEdgeStart(levelGraph.planarGraph, p.halfEdgeIndex);
      const [x2, y2] = planarGraphHalfEdgeEnd(levelGraph.planarGraph, p.halfEdgeIndex);
      const halfEdgeAngle = Math.atan2(y2 - y1, x2 - x1);
      const angle = vec2AngleBetween(x2 - x1, y2 - y1, x - x1, y - y1);
      const length = vec2Len(x - x1, y - y1);
      p.rel = (length * Math.cos(angle)) / Math.sqrt(vec2Dot(x2 - x1, y2 - y1, x2 - x1, y2 - y1));
      p.perpDist = Math.round(p.perpDist * ROUND_FACT) / ROUND_FACT;
      p.rotAngleFromBaseline = p.edgeAngle - halfEdgeAngle;

      const hash: string = hashPatternPoint(p);

      if (nearestHalfEdges[p.halfEdgeIndex] === undefined) {
        nearestHalfEdges[p.halfEdgeIndex] = { [hash]: [] };
      } else if (nearestHalfEdges[p.halfEdgeIndex][hash] === undefined) {
        nearestHalfEdges[p.halfEdgeIndex][hash] = [];
      }
      nearestHalfEdges[p.halfEdgeIndex][hash].push(p);
    }
  }

  for (const halfEdgeIndex in nearestHalfEdges) {
    const isolatedFoundationLines = nearestHalfEdges[halfEdgeIndex];

    for (const key in isolatedFoundationLines) {
      const { perpDist, foundationAssemblyId, foundationLevelId, rotAngleFromBaseline, topDatumId, bottomDatumId } =
        unHashPatternPoint(key);

      const newIsolatedFoundationPattern: GeneratedIsolatedFoundationPattern = {
        ...isolatedFoundationPatternNew({
          positions: [],
          assemblyId: foundationAssemblyId,
          xOffset: 0,
          yOffset: perpDist,
          foundationLevelId,
          pierRotation: rotAngleFromBaseline,
          topDatumId,
          bottomDatumId,
        }),
        sourcePickHandle: isolatedFoundationLines[key][0]?.sourcePickHandle,
      };

      isolatedFoundationLines[key].forEach((p) => {
        newIsolatedFoundationPattern.positions.push({ param: p.rel, paramNormalized: true, rotated: false });
      });

      if (foundationPatterns[foundationLevelId] === undefined) {
        foundationPatterns[foundationLevelId] = {};
      }
      if (foundationPatterns[foundationLevelId][halfEdgeIndex] === undefined) {
        foundationPatterns[foundationLevelId][halfEdgeIndex] = [];
      }
      foundationPatterns[foundationLevelId][halfEdgeIndex].push({ item: newIsolatedFoundationPattern });
    }
  }
  return foundationPatterns;
}

export function findNearestHalfEdgeIndex(
  halfEdgeIndices: number[],
  planarGraph: PlanarGraph,
  x: number,
  y: number,
  lineDist: NearestMeasurementMethod = NearestMeasurementMethod.MID_PT,
  checkEdgeBounds: boolean = false,
  endInclusive: boolean = true,
) {
  let minDist = Infinity;
  let minHalfEdgeIndex;
  let d: number = 0;
  for (const halfEdgeIndex of halfEdgeIndices) {
    const [x1, y1] = planarGraphHalfEdgeStart(planarGraph, halfEdgeIndex);
    const [x2, y2] = planarGraphHalfEdgeEnd(planarGraph, halfEdgeIndex);

    if (checkEdgeBounds && !vec2IsInEdgeBounds(x, y, x1, y1, x2, y2, endInclusive)) {
      continue;
    }

    switch (lineDist) {
      case NearestMeasurementMethod.MID_PT:
        const [xmid, ymid] = [(x1 + x2) / 2, (y1 + y2) / 2];
        d = vec2DistSquared(xmid, ymid, x, y);
        break;
      case NearestMeasurementMethod.PERP_DIST:
        d = line2PerpDistance(x1, y1, x2, y2, x, y);
        break;
      default:
        assertUnreachable(lineDist);
    }
    if (d < minDist) {
      minDist = d;
      minHalfEdgeIndex = halfEdgeIndex;
    }
  }
  return minHalfEdgeIndex;
}

export function pierPositionRelativeToHalfEdge(
  halfEdgeIndex: number,
  planarGraph: PlanarGraph,
  x: number,
  y: number,
  mirror: boolean = false,
): ParametricPosition {
  const [x1, y1] = planarGraphHalfEdgeStart(planarGraph, halfEdgeIndex);
  const [x2, y2] = planarGraphHalfEdgeEnd(planarGraph, halfEdgeIndex);
  const [x1m, y1m, x2m, y2m] = mirror ? [x2, y2, x1, y1] : [x1, y1, x2, y2];
  return pierParamPositionOnEdge(x1m, y1m, x2m, y2m, x, y);
}

export function getFoundationLevelIds(levels: Table<Level>): string[] {
  const groundLevels = groundLevelIds(levels);
  const bottomLevel = bottomLevelId(levels);

  const foundationLevelIds: string[] = [];

  if (bottomLevel) {
    foundationLevelIds.push(bottomLevel);
  }

  foundationLevelIds.push(...groundLevels.filter((level) => level !== bottomLevel));

  return foundationLevelIds;
}
